// Refactored Search Component - Main File (Search.jsx)
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import {
  Box, Button, Card, CardContent, CircularProgress, Collapse,
  Grid, Pagination, Typography, useMediaQuery, useTheme,
  ToggleButton, ToggleButtonGroup, Stack, Alert, AlertTitle,
  Tooltip, IconButton, Chip, Paper
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterListIcon from '@mui/icons-material/FilterList';
import SearchIcon from '@mui/icons-material/Search';

// Import components
import PickyKittyBubble from '../components/PickyKittyBubble';
import SearchFilterPanel from '../components/SearchFilterPanel';
import CardView from '../components/CardView';
import getColumns from '../components/Columns';
import FloatingCamera from '../components/FloatingCamera';
import { useFilters } from '../hooks/useFilters';
import { useDataFetching } from '../hooks/useDataFetching';
import { useEnums } from '../hooks/useEnums';
import { isProductRecentlyAdded } from '../utils/productDateUtils';
import '../style/Database.css';

// Dry Matter Basis Note Component with Tooltip
const DryMatterNote = ({ sx = {} }) => {
  const [showTooltip, setShowTooltip] = useState(false);
  
  const tooltipContent = (
    <div style={{ maxWidth: '300px' }}>
      <strong>Dry Matter Basis vs As-Fed Values:</strong><br />
      <br />
      <strong>Dry Matter Basis:</strong> Nutrient percentages calculated after removing all moisture content. This allows for accurate comparison between wet and dry foods.<br />
      <br />
      <strong>As-Fed Values:</strong> Nutrient percentages as they appear on the package, including moisture content. Wet foods appear to have lower protein/phosphorus percentages due to high water content.
    </div>
  );

  const handleClick = (event) => {
    event.stopPropagation();
    setShowTooltip(!showTooltip);
  };

  // Close tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showTooltip) {
        setShowTooltip(false);
      }
    };

    if (showTooltip) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showTooltip]);

  return (
    <Tooltip 
      title={tooltipContent}
      arrow
      placement="top"
      open={showTooltip}
      onClose={() => setShowTooltip(false)}
      disableFocusListener
      disableHoverListener
      disableTouchListener
      componentsProps={{
        tooltip: {
          sx: {
            backgroundColor: '#424242',
            color: '#ffffff',
            fontSize: '0.875rem',
            lineHeight: 1.4,
            maxWidth: '300px',
            border: '1px solid #666666',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)'
          }
        }
      }}
    >
      <Typography 
        variant="body2" 
        onClick={handleClick}
        sx={{ 
          mb: 3,
          textAlign: 'center',
          fontStyle: 'italic',
          color: 'text.secondary',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 1,
          cursor: 'pointer',
          padding: '8px 12px',
          borderRadius: '6px',
          transition: 'all 0.2s ease',
          backgroundColor: 'rgba(0, 0, 0, 0.04)',
          '&:hover': {
            color: 'text.primary',
            backgroundColor: 'rgba(0, 0, 0, 0.08)'
          },
          '&:active': {
            backgroundColor: 'rgba(0, 0, 0, 0.08)',
            transform: 'scale(0.98)'
          },
          '@media (min-width: 601px)': {
            fontSize: '1rem',
            padding: '10px 16px'
          },
          '@media (max-width: 600px)': {
            padding: '12px 16px',
            fontSize: '0.9rem',
            minHeight: '44px', // iOS recommended minimum touch target
            '&:hover': {
              backgroundColor: 'rgba(0, 0, 0, 0.08)'
            }
          },
          ...sx
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          All nutrient values shown  are on a dry matter basis.
          <InfoIcon 
            fontSize="small" 
            sx={{ 
              color: '#666666',
              opacity: 0.8,
              '&:hover': {
                opacity: 1
              }
            }} 
          />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <span style={{ color: 'blue' }}>*</span>
          Nutrient values displayed in blue indicate that the data come from guaranteed analysis (usually Min and Max values) and is not fully accurate
        </Box>
      </Typography>
    </Tooltip>
  );
};

const Database = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [cardView, setCardView] = useState(false);
  const [cardPage, setCardPage] = useState(() => {
    const saved = sessionStorage.getItem('db_cardPage');
    return saved ? parseInt(saved, 10) : 1;
  });
  const [paginationModel, setPaginationModel] = useState(() => {
    const savedPage = sessionStorage.getItem('db_page');
    const savedSize = sessionStorage.getItem('db_pageSize');
    return {
      page: savedPage ? parseInt(savedPage, 10) : 0,
      pageSize: savedSize ? parseInt(savedSize, 10) : 25,
    };
  });
  const resultsRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const cardsPerPage = 10;

  // Custom hooks for data fetching and filtering
  const { rows, filteredRows, setFilteredRows, loading, error } = useDataFetching();
  const { filters, setFilter, resetAllFilters } = useFilters(rows, setFilteredRows);
  const enums = useEnums();
  const columns = getColumns(enums);

  // Add this wrapper:
  const setFilterAndResetPage = (...args) => {
    setCardPage(1);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
    sessionStorage.setItem('db_cardPage', '1');
    sessionStorage.setItem('db_page', '0');
    setFilter(...args);
  };

  // Set default view based on screen size
  useEffect(() => {
    setCardView(isMobile);
  }, [isMobile]);

  // Reset page when filtered results change
  useEffect(() => {
    const maxPage = Math.ceil(filteredRows.length / cardsPerPage);
    if (cardPage > maxPage && maxPage > 0) {
      setCardPage(1);
    }
  }, [filteredRows, cardPage, cardsPerPage]);

  // Pagination logic
  const startIndex = (cardPage - 1) * cardsPerPage;
  const endIndex = startIndex + cardsPerPage;
  
  // Sort filtered rows with memoization to avoid re-sorting on every render
  const sortedFilteredRows = useMemo(() => {
    return [...filteredRows].sort((a, b) => {
      const brandComparison = (a.brand || '').localeCompare(b.brand || '');
      if (brandComparison !== 0) return brandComparison;
      const lineComparison = (a.line || '').localeCompare(b.line || '');
      if (lineComparison !== 0) return lineComparison;
      return (a.name || '').localeCompare(b.name || '');
    });
  }, [filteredRows]);

  const paginatedCards = sortedFilteredRows.slice(startIndex, endIndex);

  // Check if there are active filters
  const hasActiveFilters = () => {
    // Check text filters
    if (filters.text.searchText && filters.text.searchText.trim() !== '') {
      return true;
    }
    
    // Check multiselect filters
    for (const key in filters.multiSelect) {
      if (Array.isArray(filters.multiSelect[key]) && filters.multiSelect[key].length > 0) {
        return true;
      }
    }
    
    // Check boolean filters
    for (const key in filters.boolean) {
      if (Array.isArray(filters.boolean[key]) && filters.boolean[key].length > 0) {
        return true;
      }
    }
    
    // Check range filters
    for (const key in filters.range) {
      if ((filters.range[key].min !== undefined && filters.range[key].min !== '') ||
          (filters.range[key].max !== undefined && filters.range[key].max !== '')) {
        return true;
      }
    }
    
    return false;
  };
  const totalPages = Math.ceil(filteredRows.length / cardsPerPage);

  const handleCardViewChange = (event, value) => {
    if (value === null) return;
    setCardView(value === 'card');
  };

  return (
    <div className="search-container">

      <div className="page-header">
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
          <h1 className="page-title">Cat Food Database</h1>
          <Typography 
            variant="body2" 
            sx={{ 
              mt: 0.5,
              color: 'text.secondary',
              fontSize: { xs: '1rem', sm: '1.1rem' },
              textAlign: 'center'
            }}
          >
            Showing {filteredRows.length} products • Filter to narrow results
          </Typography>
        </Box>
      </div>

      {/* Information Banner about actively adding products
      <Box
        sx={{
          mb: { xs: 2, sm: 3 },
          p: 2,
          border: '1px solid #e3f2fd',
          borderRadius: 3,
          textAlign: 'center',
          backgroundColor: '#f3f8ff',
          boxShadow: '0 2px 8px rgba(33, 150, 243, 0.1)'
        }}
      >
        <Typography 
          variant="body2" 
          sx={{ 
            fontSize: '0.9rem',
            lineHeight: 1.5,
            color: '#424242',
            margin: 0,
            fontWeight: 500
          }}
        >
          I’m always adding new products to my database, come back soon to see what’s new!

        </Typography>
      </Box> */}

      {/* Cat image on mobile - outside filter section */}
      {isMobile && (
        <Box sx={{ width: '90%', mb: 1, mt: -1, display: 'flex', justifyContent: 'center' }}>
          <div className="search-image-container">
            <img
              src="/picky-kitty/search.png"
              alt="Search"
              className="search-image"
            />
          </div>
        </Box>
      )}

      {/* Mobile Filter Toggle Button */}
      {isMobile && (
        <Box sx={{ width: '90%', mb: 2, display: 'flex', flexDirection: 'column', gap: 1.5, alignItems: 'center' }}>
          <Button
            variant="contained"
            disableRipple
            onClick={() => setMobileFiltersOpen(!mobileFiltersOpen)}
            startIcon={
              mobileFiltersOpen ? (
                <ExpandLessIcon />
              ) : (
                <SearchIcon sx={{ fontSize: '1.2rem' }} />
              )
            }
            sx={{
              width: '100%',
              color: '#fff',
              backgroundColor: '#42a5f5 !important',
              border: 'none',
              textTransform: 'none',
              fontWeight: 700,
              fontSize: '1.05rem',
              padding: '12px 20px',
              borderRadius: '12px',
              boxShadow: 'none !important',
              position: 'relative',
              '&:hover': {
                backgroundColor: '#1976d2 !important'
              },
              '&:focus': {
                boxShadow: 'none !important',
                outline: 'none',
                backgroundColor: '#42a5f5 !important'
              },
              '&:focus-visible': {
                boxShadow: 'none !important',
                outline: 'none',
                backgroundColor: '#42a5f5 !important'
              },
              '&:active': {
                boxShadow: 'none !important',
                backgroundColor: '#42a5f5 !important'
              },
              '&.Mui-focusVisible': {
                backgroundColor: '#42a5f5 !important',
                boxShadow: 'none !important'
              },
              '& .MuiButton-startIcon': {
                marginRight: '10px',
                marginLeft: 0
              }
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', justifyContent: 'space-between' }}>
              <span>{mobileFiltersOpen ? 'Hide Filters' : 'Search'}</span>
              {!hasActiveFilters() && !mobileFiltersOpen && (
                <Chip
                  label="Tap to filter"
                  size="small"
                  sx={{
                    height: '24px',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    color: '#fff',
                    '& .MuiChip-label': {
                      px: 1.5,
                      py: 0.5
                    }
                  }}
                />
              )}
              {hasActiveFilters() && !mobileFiltersOpen && (
                <Chip
                  label="Active"
                  size="small"
                  sx={{
                    height: '24px',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    backgroundColor: 'rgba(255, 255, 255, 0.2)',
                    color: '#fff',
                    '& .MuiChip-label': {
                      px: 1.5,
                      py: 0.5
                    }
                  }}
                />
              )}
            </Box>
          </Button>
          {/* Clear All Filters Button - visible when filters are active and collapsed */}
          {hasActiveFilters() && !mobileFiltersOpen && (
            <Button
              variant="outlined"
              disableRipple
              onClick={resetAllFilters}
              sx={{
                width: '100%',
                color: '#42a5f5',
                backgroundColor: 'transparent !important',
                borderColor: '#42a5f5',
                borderWidth: '2px',
                textTransform: 'none',
                fontWeight: 700,
                fontSize: '1.05rem',
                padding: '12px 20px',
                borderRadius: '12px',
                boxShadow: 'none !important',
                position: 'relative',
                '&:hover': {
                  backgroundColor: 'transparent !important',
                  borderColor: '#1976d2',
                  color: '#1976d2'
                },
                '&:focus': {
                  boxShadow: 'none !important',
                  outline: 'none',
                  backgroundColor: 'transparent !important',
                  borderColor: '#42a5f5'
                },
                '&:focus-visible': {
                  boxShadow: 'none !important',
                  outline: 'none',
                  backgroundColor: 'transparent !important',
                  borderColor: '#42a5f5'
                },
                '&:active': {
                  boxShadow: 'none !important',
                  backgroundColor: 'transparent !important',
                  borderColor: '#42a5f5'
                },
                '&.Mui-focusVisible': {
                  backgroundColor: 'transparent !important',
                  boxShadow: 'none !important',
                  borderColor: '#42a5f5'
                }
              }}
            >
              Clear All Filters
            </Button>
          )}
        </Box>
      )}

      {/* Search filter section with image on right */}
      <Collapse in={!isMobile || mobileFiltersOpen}>
        {isMobile ? (
          <Box sx={{ width: '90%', mx: 'auto', mb: 3 }}>
            <Paper
              elevation={2}
              sx={{
                p: 2.5,
                borderRadius: 2,
                backgroundColor: '#fafafa',
                border: hasActiveFilters() ? '2px solid #1976d2' : '1px solid #e0e0e0',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                transition: 'all 0.3s ease-in-out',
                overflow: 'hidden',
                '& .search-filter-container': {
                  padding: '0 !important',
                  margin: 0,
                  maxWidth: '100%',
                  overflow: 'hidden'
                }
              }}
            >
              <SearchFilterPanel
                filters={filters}
                setFilter={setFilterAndResetPage}
                open={open}
                setOpen={setOpen}
                resetFilters={resetAllFilters}
              />
            </Paper>
          </Box>
        ) : (
          <div className="search-page-filter-section">
            <SearchFilterPanel
              filters={filters}
              setFilter={setFilterAndResetPage}
              open={open}
              setOpen={setOpen}
              resetFilters={resetAllFilters}
            />
            
            <div className="search-image-container">
              <img
                src="/picky-kitty/search.png"
                alt="Search"
                className="search-image"
              />
            </div>
          </div>
        )}
      </Collapse>

      {isMobile && (
        <ToggleButtonGroup
          value={cardView ? 'card' : 'table'}
          exclusive
          onChange={handleCardViewChange}
          sx={{ marginBottom: 2 }}
        >
          <ToggleButton value="card">Card View</ToggleButton>
          <ToggleButton value="table">Table View</ToggleButton>
        </ToggleButtonGroup>
      )}

      <div id="db-results-top" ref={resultsRef} style={{ scrollMarginTop: 80 }} />
      <div className="data-grid-container">
        {loading ? (
          <div className="loading-container">
            <CircularProgress />
          </div>
        ) : error ? (
          <div className="error-message">{error}</div>
        ) : (!isMobile || !cardView) ? (
          // Show DataGrid on desktop or mobile + table view
          <>
            {/* Dry matter basis note - shown on desktop above table */}
            {!isMobile && <DryMatterNote sx={{ mt: 2, mb: 2 }} />}
            
            <DataGrid
              rows={filteredRows}
              columns={columns}
              pageSizeOptions={[10, 25, 50, 100]}
              paginationModel={paginationModel}
              onPaginationModelChange={(model) => {
                const pageChanged = model.page !== paginationModel.page;
                setPaginationModel(model);
                sessionStorage.setItem('db_page', String(model.page));
                sessionStorage.setItem('db_pageSize', String(model.pageSize));
                if (pageChanged) {
                  resultsRef.current?.scrollIntoView({ behavior: 'smooth' });
                }
              }}
              slots={{ toolbar: GridToolbar }}
              slotProps={{
                toolbar: {
                  printOptions: { disableToolbarButton: true },
                  csvOptions: { disableToolbarButton: true },
                },
                pagination: {
                  showFirstButton: true,
                  showLastButton: true,
                },
              }}
              getRowClassName={(params) =>
                [
                  params.indexRelativeToCurrentPage % 2 === 0 ? 'Mui-even' : 'Mui-odd',
                  isProductRecentlyAdded(params.row.createDate) ? 'Mui-recent' : ''
                ].filter(Boolean).join(' ')
              }
              disableColumnFilter
              disableDataExport
              localeText={{
                noRowsLabel: 'No cat food products match your current search. Try broadening your filters or using different search terms to find what you need.',
              }}
                             initialState={{
                sorting: {
                  sortModel: [
                    { field: 'brand', sort: 'asc' },
                    { field: 'line', sort: 'asc' },
                    { field: 'name', sort: 'asc' }
                  ],
                },
                columns: {
                  columnVisibilityModel: {
                    texture: false,
                    format: false,
                    isGrainFree: false,
                    isGmoFree: false,
                    isLid: false,
                    containsRawFood: false,
                    isPrescription: false,
                    healthBenefits: false,
                  },
                },
              }}
            />
            
            {/* Dry matter basis note - shown on mobile after table */}
            {isMobile && <DryMatterNote sx={{ mt: 4 }} />}
          </>
        ) : (
          // Show Card view on mobile when cardView is true
          <Box sx={{ width: '100%' }}>
            <CardView cards={paginatedCards} />

            {/* Pagination + total count */}
            <Box display="flex" flexDirection="column" alignItems="center" mt={2} mb={4}>
              <Typography variant="body2">Total results: {filteredRows.length}</Typography>
              <Pagination
                count={totalPages}
                page={cardPage}
                showFirstButton
                showLastButton
                onChange={(_, page) => {
                  setCardPage(page);
                  sessionStorage.setItem('db_cardPage', String(page));
                  requestAnimationFrame(() => {
                    requestAnimationFrame(() => {
                      const el = document.getElementById('db-results-top');
                      if (el) el.scrollIntoView(true);
                    });
                  });
                }}
                sx={{ mt: 1 }}
              />
            </Box>

            {/* Dry matter basis note - shown on mobile in card view */}
            {isMobile && (
              <Box sx={{ mt: 0 }}>
                <DryMatterNote />
              </Box>
            )}
          </Box>
        )}
      </div>

      {/* Picky Kitty Speech Bubble */}
      <PickyKittyBubble mt={{ xs: 1.5, sm: 3 }}>
        Can't find what you're looking for? Take a photo and I'll find it for you!{' '}
        <Box
          component="a"
          href="/Scanner"
          sx={{
            color: '#1976d2',
            textDecoration: 'none',
            fontWeight: 600,
            borderBottom: '2px solid #1976d2',
            transition: 'all 0.2s ease',
            display: 'inline-block',
            '&:hover': {
              color: '#1565c0',
              borderBottomColor: '#1565c0',
              transform: 'translateY(-1px)'
            }
          }}
        >
          Try the Scanner
        </Box>
      </PickyKittyBubble>

      {/* Footnote section moved outside data-grid-container */}
      {!isMobile && (
        <Box sx={{ 
          mt: 1.5, 
          mb: 1.5, 
          mx: 1.5,
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          gap: 1.2 
        }}>         
          {cardView && (
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'text.secondary',
                fontStyle: 'italic',
                textAlign: 'center',
                mt: 1
              }}
            >
              As an Amazon Associate, The Picky Kitty earns from qualifying purchases.{" "}
              <a 
                href="/amazon-disclosure" 
                style={{ 
                  color: 'inherit', 
                  textDecoration: 'underline',
                  fontWeight: '500'
                }}
              >
                Learn more
              </a>
            </Typography>
          )}
        </Box>
      )}
      
      {/* Floating Camera Button */}
      <FloatingCamera />
    </div>
  );
};

export default Database;